import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Send, FileText, ChevronDown, ChevronUp, Trash2, Calendar, Check, Users, User } from 'lucide-react';
import { Button } from '../components/ui/button';
import VoiceInput from '../components/VoiceInput';

const RapportJournalier = ({ user }) => {
  const [categories, setCategories] = useState({});
  const [selectedItems, setSelectedItems] = useState({});
  const [texteLibre, setTexteLibre] = useState('');
  const [dateRapport, setDateRapport] = useState(new Date().toISOString().split('T')[0]);
  const [sending, setSending] = useState(false);
  const [rapports, setRapports] = useState([]);
  const [expandedRapport, setExpandedRapport] = useState(null);
  const [stagiaires, setStagiaires] = useState([]);
  const [selectedStagiaires, setSelectedStagiaires] = useState([]);

  useEffect(() => {
    loadCategories();
    loadRapports();
    if (user?.role === 'formateur') loadStagiaires();
  }, []);

  const loadCategories = async () => {
    try {
      const res = await axios.get('/rapports-journaliers/categories');
      setCategories(res.data);
    } catch {}
  };

  const loadRapports = async () => {
    try {
      const res = await axios.get('/rapports-journaliers');
      setRapports(res.data);
    } catch {}
  };

  const loadStagiaires = async () => {
    try {
      const res = await axios.get('/rapports-journaliers/stagiaires-liste');
      setStagiaires(res.data);
    } catch {}
  };

  const toggleItem = (catKey, item) => {
    setSelectedItems(prev => {
      const catItems = prev[catKey] || [];
      if (catItems.includes(item)) {
        return { ...prev, [catKey]: catItems.filter(i => i !== item) };
      }
      return { ...prev, [catKey]: [...catItems, item] };
    });
  };

  const toggleStagiaire = (uid) => {
    setSelectedStagiaires(prev =>
      prev.includes(uid) ? prev.filter(id => id !== uid) : [...prev, uid]
    );
  };

  const toggleAllStagiaires = () => {
    if (selectedStagiaires.length === stagiaires.length) {
      setSelectedStagiaires([]);
    } else {
      setSelectedStagiaires(stagiaires.map(s => s.user_id));
    }
  };

  const handleSubmit = async () => {
    const hasItems = Object.values(selectedItems).some(arr => arr.length > 0);
    if (!hasItems && !texteLibre.trim()) {
      toast.error('Sélectionnez au moins un item ou ajoutez du texte');
      return;
    }
    setSending(true);
    try {
      const stagiairesData = selectedStagiaires.map(uid => {
        const s = stagiaires.find(st => st.user_id === uid);
        return s ? { user_id: s.user_id, prenom: s.prenom, nom: s.nom } : { user_id: uid };
      });
      await axios.post('/rapports-journaliers', {
        selected_items: selectedItems,
        selected_stagiaires: stagiairesData,
        texte_libre: texteLibre,
        date: dateRapport
      });
      toast.success('Rapport envoyé au centre');
      setSelectedItems({});
      setSelectedStagiaires([]);
      setTexteLibre('');
      loadRapports();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setSending(false);
    }
  };

  const handleDelete = async (rapportId) => {
    if (!window.confirm('Supprimer ce rapport ?')) return;
    try {
      await axios.delete(`/rapports-journaliers/${rapportId}`);
      toast.success('Rapport supprimé');
      loadRapports();
    } catch {
      toast.error('Erreur');
    }
  };

  const totalSelected = Object.values(selectedItems).reduce((sum, arr) => sum + arr.length, 0);

  const catColors = {
    participation: 'border-blue-200 bg-blue-50/30',
    comprehension: 'border-emerald-200 bg-emerald-50/30',
    motivation: 'border-amber-200 bg-amber-50/30',
    dynamique_groupe: 'border-purple-200 bg-purple-50/30',
    travail_exercices: 'border-cyan-200 bg-cyan-50/30',
    assiduite_comportement: 'border-rose-200 bg-rose-50/30',
    ressenti_global: 'border-indigo-200 bg-indigo-50/30',
    meteo: 'border-sky-200 bg-sky-50/30',
    theme: 'border-orange-200 bg-orange-50/30'
  };

  const chipColors = {
    participation: 'bg-blue-100 text-blue-700 border-blue-300 hover:bg-blue-200',
    comprehension: 'bg-emerald-100 text-emerald-700 border-emerald-300 hover:bg-emerald-200',
    motivation: 'bg-amber-100 text-amber-700 border-amber-300 hover:bg-amber-200',
    dynamique_groupe: 'bg-purple-100 text-purple-700 border-purple-300 hover:bg-purple-200',
    travail_exercices: 'bg-cyan-100 text-cyan-700 border-cyan-300 hover:bg-cyan-200',
    assiduite_comportement: 'bg-rose-100 text-rose-700 border-rose-300 hover:bg-rose-200',
    ressenti_global: 'bg-indigo-100 text-indigo-700 border-indigo-300 hover:bg-indigo-200',
    meteo: 'bg-sky-100 text-sky-700 border-sky-300 hover:bg-sky-200',
    theme: 'bg-orange-100 text-orange-700 border-orange-300 hover:bg-orange-200'
  };

  const chipSelectedColors = {
    participation: 'bg-blue-600 text-white border-blue-600',
    comprehension: 'bg-emerald-600 text-white border-emerald-600',
    motivation: 'bg-amber-600 text-white border-amber-600',
    dynamique_groupe: 'bg-purple-600 text-white border-purple-600',
    travail_exercices: 'bg-cyan-600 text-white border-cyan-600',
    assiduite_comportement: 'bg-rose-600 text-white border-rose-600',
    ressenti_global: 'bg-indigo-600 text-white border-indigo-600',
    meteo: 'bg-sky-600 text-white border-sky-600',
    theme: 'bg-orange-600 text-white border-orange-600'
  };

  const meteoEmojis = {
    'Ciel couvert / gris': '☁️',
    'Beau soleil': '☀️',
    'Pluie': '🌧️',
    'Vent faible': '🍃',
    'Vent modéré': '💨',
    'Vent fort': '🌬️',
    'Orageux': '⛈️',
    'Mer calme': '🌊',
    'Petit clapot': '🌊',
    'Mer agitée': '🌊'
  };

  const isFormateur = user?.role === 'formateur';
  const isCentre = user?.role === 'centre' || user?.role === 'responsable_pedagogique';
  const formateurName = isFormateur ? `${user?.prenom || ''} ${user?.nom || ''}`.trim() : '';

  return (
    <div className="space-y-6" data-testid="rapport-journalier-page">
      <div>
        <h1 className="text-4xl font-display font-bold text-[#0F172A] mb-1">
          {isCentre ? 'Rapports Formateurs' : 'Rapport Journalier'}
        </h1>
        <p className="text-slate-600">
          {isCentre ? 'Consultez les rapports journaliers des formateurs' : 'Envoyez un résumé de la journée au centre de formation'}
        </p>
      </div>

      {/* Formulaire - uniquement pour formateur */}
      {isFormateur && (
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        {/* En-tête : Date + Formateur */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between p-4 border-b border-slate-100 gap-3">
          <div className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-[#8DC63F]" />
            <span className="font-semibold text-slate-700">Date du rapport</span>
            <input
              type="date"
              value={dateRapport}
              onChange={e => setDateRapport(e.target.value)}
              className="ml-2 px-3 py-1.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#8DC63F]"
              data-testid="rapport-date-input"
            />
          </div>
          <div className="flex items-center gap-2 bg-[#8DC63F]/10 px-3 py-1.5 rounded-lg">
            <User className="h-4 w-4 text-[#8DC63F]" />
            <span className="text-sm font-semibold text-[#6BA82A]" data-testid="rapport-formateur-name">{formateurName}</span>
          </div>
        </div>

        {/* Sélection des stagiaires */}
        <div className="p-4 border-b border-slate-100">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Users className="h-5 w-5 text-[#8DC63F]" />
              <span className="text-sm font-bold text-slate-700">Stagiaires du groupe</span>
              <span className="text-xs text-slate-400">({selectedStagiaires.length}/{stagiaires.length})</span>
            </div>
            <button
              onClick={toggleAllStagiaires}
              className="text-xs text-[#8DC63F] hover:text-[#6BA82A] font-medium"
              data-testid="toggle-all-stagiaires"
            >
              {selectedStagiaires.length === stagiaires.length ? 'Tout décocher' : 'Tout cocher'}
            </button>
          </div>
          {stagiaires.length === 0 ? (
            <p className="text-xs text-slate-400 italic">Aucun stagiaire trouvé</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {stagiaires.map(s => {
                const checked = selectedStagiaires.includes(s.user_id);
                return (
                  <label
                    key={s.user_id}
                    className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs font-medium cursor-pointer transition-all ${
                      checked
                        ? 'bg-[#8DC63F] text-white border-[#8DC63F]'
                        : 'bg-white text-slate-600 border-slate-200 hover:border-[#8DC63F]/50 hover:bg-[#8DC63F]/5'
                    }`}
                    data-testid={`stagiaire-check-${s.user_id}`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleStagiaire(s.user_id)}
                      className="sr-only"
                    />
                    {checked && <Check className="h-3 w-3" />}
                    {s.prenom} {s.nom}
                  </label>
                );
              })}
            </div>
          )}
        </div>

        {/* Categories */}
        <div className="p-4 space-y-4">
          {Object.entries(categories).map(([catKey, cat]) => {
            const catItems = selectedItems[catKey] || [];
            return (
              <div key={catKey} className={`rounded-lg border p-3 ${catColors[catKey] || 'border-slate-200 bg-slate-50/30'}`}>
                <p className="text-sm font-bold text-slate-700 mb-2">{cat.label}</p>
                <div className="flex flex-wrap gap-1.5">
                  {cat.items.map(item => {
                    const isSelected = catItems.includes(item);
                    return (
                      <button
                        key={item}
                        onClick={() => toggleItem(catKey, item)}
                        className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-all cursor-pointer ${
                          isSelected
                            ? chipSelectedColors[catKey] || 'bg-[#8DC63F] text-white border-[#8DC63F]'
                            : chipColors[catKey] || 'bg-slate-100 text-slate-600 border-slate-300 hover:bg-slate-200'
                        }`}
                        data-testid={`item-${catKey}-${item.substring(0, 15).replace(/\s/g, '-')}`}
                      >
                        {isSelected && <Check className="h-3 w-3 inline mr-1" />}
                        {catKey === 'meteo' && meteoEmojis[item] ? `${meteoEmojis[item]} ` : ''}{item}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {/* Texte libre */}
        <div className="px-4 pb-4">
          <div className="flex items-center gap-2 mb-1.5">
            <label className="text-sm font-bold text-slate-700">Commentaire libre (optionnel)</label>
            <VoiceInput
              onTranscript={(text) => setTexteLibre(prev => prev ? `${prev} ${text}` : text)}
              className="w-8 h-8"
            />
          </div>
          <textarea
            value={texteLibre}
            onChange={e => setTexteLibre(e.target.value)}
            placeholder="Ajoutez vos remarques personnelles sur la journée... ou utilisez le micro"
            className="w-full h-24 px-3 py-2 border border-slate-200 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#8DC63F]"
            data-testid="rapport-texte-libre"
          />
        </div>

        {/* Submit */}
        <div className="px-4 pb-4 flex items-center justify-between">
          <span className="text-sm text-slate-500">
            {totalSelected > 0 ? `${totalSelected} item${totalSelected > 1 ? 's' : ''} sélectionné${totalSelected > 1 ? 's' : ''}` : 'Aucun item sélectionné'}
            {selectedStagiaires.length > 0 && ` | ${selectedStagiaires.length} stagiaire${selectedStagiaires.length > 1 ? 's' : ''}`}
          </span>
          <Button
            onClick={handleSubmit}
            disabled={sending}
            className="bg-[#8DC63F] hover:bg-[#7db336] text-white"
            data-testid="rapport-submit-btn"
          >
            <Send className="h-4 w-4 mr-2" />
            {sending ? 'Envoi...' : 'Envoyer au centre'}
          </Button>
        </div>
      </div>
      )}

      {/* Historique */}
      {(rapports.length > 0 || isCentre) && (
        <div>
          <h2 className="text-lg font-bold text-[#0F172A] mb-3">
            {isCentre ? 'Rapports reçus' : 'Historique des rapports'}
          </h2>
          {rapports.length === 0 && (
            <div className="bg-white rounded-lg border border-dashed border-slate-300 p-8 text-center">
              <FileText className="h-8 w-8 text-slate-300 mx-auto mb-2" />
              <p className="text-slate-500">Aucun rapport reçu pour le moment</p>
            </div>
          )}
          <div className="space-y-2">
            {rapports.map(r => {
              const isExpanded = expandedRapport === r.rapport_id;
              const itemCount = Object.values(r.selected_items || {}).reduce((sum, arr) => sum + arr.length, 0);
              const stagCount = (r.selected_stagiaires || []).length;
              return (
                <div key={r.rapport_id} className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
                  <div
                    className="flex items-center justify-between p-3 cursor-pointer hover:bg-slate-50 transition-colors"
                    onClick={() => setExpandedRapport(isExpanded ? null : r.rapport_id)}
                    data-testid={`rapport-${r.rapport_id}`}
                  >
                    <div className="flex items-center gap-3">
                      <FileText className="h-4 w-4 text-[#8DC63F]" />
                      <div>
                        <span className="text-sm font-semibold text-slate-800">{r.date}</span>
                        {r.formateur_nom && <span className="text-sm text-slate-600 ml-2">— {r.formateur_nom}</span>}
                        <span className="text-xs text-slate-400 ml-2">{itemCount} item{itemCount > 1 ? 's' : ''}</span>
                        {stagCount > 0 && <span className="text-xs text-slate-400 ml-1">| {stagCount} stagiaire{stagCount > 1 ? 's' : ''}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {user?.role !== 'centre' && user?.role !== 'responsable_pedagogique' && (
                        <button
                          onClick={e => { e.stopPropagation(); handleDelete(r.rapport_id); }}
                          className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded"
                          data-testid={`delete-rapport-${r.rapport_id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                      {isExpanded ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
                    </div>
                  </div>
                  {isExpanded && (
                    <div className="px-4 pb-3 border-t border-slate-100 pt-3 space-y-2">
                      {r.formateur_nom && (
                        <p className="text-xs text-slate-500 font-medium flex items-center gap-1">
                          <User className="h-3 w-3" /> Formateur : {r.formateur_nom}
                        </p>
                      )}
                      {/* Stagiaires du groupe */}
                      {(r.selected_stagiaires || []).length > 0 && (
                        <div>
                          <p className="text-xs font-bold text-slate-600 flex items-center gap-1">
                            <Users className="h-3 w-3" /> Stagiaires du groupe ({r.selected_stagiaires.length})
                          </p>
                          <div className="flex flex-wrap gap-1 mt-0.5">
                            {r.selected_stagiaires.map((s, i) => (
                              <span key={s.user_id || i} className="px-2 py-0.5 rounded-full text-[10px] font-medium border bg-[#8DC63F]/10 text-[#6BA82A] border-[#8DC63F]/30">
                                {s.prenom} {s.nom}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      {Object.entries(r.selected_items || {}).map(([catKey, items]) => {
                        if (!items || items.length === 0) return null;
                        const catLabel = categories[catKey]?.label || catKey;
                        return (
                          <div key={catKey}>
                            <p className="text-xs font-bold text-slate-600">{catLabel}</p>
                            <div className="flex flex-wrap gap-1 mt-0.5">
                              {items.map(item => (
                                <span key={item} className={`px-2 py-0.5 rounded-full text-[10px] font-medium border ${chipSelectedColors[catKey] || 'bg-[#8DC63F] text-white'}`}>
                                  {catKey === 'meteo' && meteoEmojis[item] ? `${meteoEmojis[item]} ` : ''}{item}
                                </span>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                      {r.texte_libre && (
                        <div className="bg-slate-50 rounded p-2 mt-2">
                          <p className="text-xs text-slate-600 italic">"{r.texte_libre}"</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default RapportJournalier;
